(() => {
  'use strict';
  if (customElements.get('bm-gm3-gallery-3d')) return;

  const nativeLoads = new Map();
  const nativeModelSetups = new WeakMap();
  function loadNative(url) {
    if (customElements.get('product-model')) return Promise.resolve();
    if (nativeLoads.has(url)) return nativeLoads.get(url);
    const pending = new Promise((resolve, reject) => {
      if (!url || typeof DeferredMedia === 'undefined') { reject(new Error('Native media unavailable')); return; }
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      let timer = setTimeout(() => { script.remove(); reject(new Error('Native media timed out')); }, 15000);
      script.onload = () => {
        clearTimeout(timer);
        if (customElements.get('product-model')) resolve();
        else reject(new Error('Native media unavailable'));
      };
      script.onerror = () => { clearTimeout(timer); script.remove(); reject(new Error('Native media unavailable')); };
      document.head.append(script);
    }).catch(error => { nativeLoads.delete(url); throw error; });
    nativeLoads.set(url, pending);
    return pending;
  }

  customElements.define('bm-gm3-gallery-3d', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.host = this.closest('[data-gm3-configurator]');
      this.gallery = this.host?.querySelector('[data-gallery]');
      this.photo = this.host?.querySelector('.bmc-photo');
      this.visual = this.host?.querySelector('.bmc-visual');
      this.stage = this.querySelector('[data-gm3-model-stage]');
      this.thumbItem = this.querySelector('.bmc-3d-thumbnail');
      this.thumb = this.thumbItem?.querySelector('button');
      if (!this.host || !this.gallery || !this.photo || !this.stage || !this.thumb) return;
      this.initialized = true;
      this.generation = (this.generation || 0) + 1;
      this.pm = this.stage.querySelector('product-model');
      this.loading = this.stage.querySelector('[data-gm3-model-loading]');
      this.error = this.stage.querySelector('[data-gm3-model-error]');
      this.xr = this.stage.querySelector('[data-shopify-xr]');
      this.photo.append(this.stage);
      this.attachThumbnail();
      this.thumb.disabled = false;
      this.onThumb = () => this.show();
      this.thumb.addEventListener('click', this.onThumb);
      this.onStage = event => {
        const button = event.target.closest('button');
        if (!button) return;
        if (button.matches('.deferred-media__poster, [data-gm3-model-retry], [data-shopify-xr]')) {
          event.preventDefault(); event.stopImmediatePropagation();
          if (button === this.xr) this.activateAR();
          else this.loadModel(button.hasAttribute('data-gm3-model-retry'));
        } else if (button.hasAttribute('data-gm3-model-close')) {
          event.preventDefault(); event.stopPropagation(); this.close(true);
        }
      };
      this.stage.addEventListener('click', this.onStage, true);
      this.onHostClick = event => {
        if (event.target.closest('[data-gallery-index], [data-model], [data-package], [data-select-model], [data-select-variant], [data-explore-package]')) this.close(false);
      };
      this.onChange = event => { if (event.target.matches('input[name="id"]')) this.close(false); };
      this.onKey = event => {
        if (event.key === 'Escape' && !this.stage.hidden) { event.preventDefault(); this.close(true); }
      };
      this.host.addEventListener('click', this.onHostClick, true);
      this.host.addEventListener('change', this.onChange);
      this.host.addEventListener('keydown', this.onKey);
      this.observer = new MutationObserver(() => this.attachThumbnail());
      this.observer.observe(this.gallery, { childList: true });
    }

    attachThumbnail() {
      if (!this.gallery.contains(this.thumbItem)) this.gallery.append(this.thumbItem);
    }

    show() {
      this.host.closeMainPanel?.(false);
      this.stage.hidden = false;
      this.photo.classList.add('bmc-3d-active');
      this.visual.classList.add('bmc-3d-active');
      this.thumb.setAttribute('aria-pressed', 'true');
      this.gallery.querySelectorAll('[data-gallery-index]').forEach(button => button.setAttribute('aria-pressed', 'false'));
      if (![...document.querySelectorAll('link[rel="stylesheet"]')].some(link => link.href === this.dataset.uiStyle)) {
        const style = document.createElement('link');
        style.rel = 'stylesheet';
        style.href = this.dataset.uiStyle;
        document.head.append(style);
      }
      // The native module defines product-model only. Model data remains inside its template until the poster is pressed.
      const generation = this.generation;
      loadNative(this.dataset.nativeSrc).catch(() => this.fail(generation));
      const focusTarget = this.viewer?.loaded ? this.viewer : this.pm.querySelector('.deferred-media__poster');
      focusTarget?.focus({ preventScroll: true });
    }

    close(restoreFocus) {
      if (this.stage.hidden) return;
      const hadFocus = this.stage.contains(document.activeElement);
      this.stage.hidden = true;
      this.photo.classList.remove('bmc-3d-active');
      this.visual.classList.remove('bmc-3d-active');
      this.thumb.setAttribute('aria-pressed', 'false');
      this.gallery.querySelectorAll('[data-gallery-index]').forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.galleryIndex) === (this.host.galleryIndex || 0))));
      this.pm.modelViewerUI?.pause?.();
      if (restoreFocus || hadFocus) this.thumb.focus({ preventScroll: true });
    }

    async loadModel(retry = false) {
      if (this.pending || (this.viewer?.loaded && !retry)) return;
      const generation = ++this.generation;
      this.pending = true;
      this.error.hidden = true;
      this.loading.hidden = false;
      this.xr.hidden = true;
      this.xr.disabled = true;
      this.stage.dataset.ar = 'no';
      this.stage.dataset.state = 'loading';
      clearTimeout(this.loadTimer);
      this.loadTimer = setTimeout(() => this.fail(generation), 45000);
      try {
        await loadNative(this.dataset.nativeSrc);
        if (!this.current(generation)) return;
        if (retry) {
          this.pm.modelViewerUI?.destroy?.();
          this.pm.modelViewerUI = null;
          this.pm.querySelectorAll('model-viewer, .shopify-model-viewer-ui').forEach(node => node.remove());
          this.pm.removeAttribute('loaded');
        }
        const model = this.pm;
        const nativeSetup = nativeModelSetups.get(model) || model.setupModelViewerUI;
        nativeModelSetups.set(model, nativeSetup);
        let expectedViewer = null;
        this.viewer = null;
        model.setupModelViewerUI = errors => {
          if (!this.current(generation) || !model.isConnected) return;
          // A cached feature can call back synchronously, after native loadContent has mounted the viewer.
          const viewer = expectedViewer || model.querySelector('model-viewer');
          if (!viewer || model.querySelector('model-viewer') !== viewer || (this.viewer && this.viewer !== viewer)) return;
          expectedViewer = viewer;
          this.viewer = viewer;
          if (this.current(generation, viewer)) nativeSetup.call(model, errors);
        };
        model.loadContent();
        expectedViewer = model.querySelector('model-viewer');
        this.viewer = expectedViewer;
        if (!this.viewer) throw new Error('Native model unavailable');
        const viewer = this.viewer;
        viewer.setAttribute('ar-scale', 'fixed');
        viewer.addEventListener('load', () => this.loaded(generation, viewer), { once: true });
        viewer.addEventListener('error', () => this.fail(generation, viewer), { once: true });
        viewer.addEventListener('ar-status', event => { if (event.detail?.status === 'failed') this.fail(generation, viewer); });
        if (viewer.loaded) this.loaded(generation, viewer);
        else customElements.whenDefined('model-viewer').then(() => { if (viewer.loaded) this.loaded(generation, viewer); });
      } catch (error) { this.fail(generation); }
    }

    current(generation, viewer) {
      return this.initialized && this.isConnected && generation === this.generation && (!viewer || viewer === this.viewer);
    }

    loaded(generation, viewer) {
      if (!this.current(generation, viewer)) return;
      clearTimeout(this.loadTimer);
      this.pending = false;
      this.stage.dataset.state = 'loaded';
      this.error.hidden = true;
      this.loading.hidden = true;
      clearInterval(this.arTimer);
      let remaining = 15;
      const update = () => {
        if (!this.current(generation, viewer)) { clearInterval(this.arTimer); return; }
        const available = !!this.viewer?.canActivateAR;
        this.xr.hidden = !available;
        this.xr.disabled = !available;
        const arState = available ? 'yes' : 'no';
        if (this.stage.dataset.ar !== arState) {
          this.stage.dataset.ar = arState;
          // Changing the child height lets the native viewer's ResizeObserver reframe its canvas.
          requestAnimationFrame(() => { if (this.current(generation, viewer)) viewer.requestUpdate?.(); });
        }
        if (available || --remaining <= 0) clearInterval(this.arTimer);
      };
      this.arTimer = setInterval(update, 200);
      update();
    }

    async activateAR() {
      if (!this.viewer?.loaded || !this.viewer.canActivateAR || this.xr.disabled) return;
      const generation = this.generation;
      const viewer = this.viewer;
      try {
        // Use the native model-viewer launcher so fixed scale applies to Quick Look and Scene Viewer.
        await viewer.activateAR();
      } catch (error) { this.fail(generation, viewer); }
    }

    fail(generation, viewer) {
      if (!this.current(generation, viewer)) return;
      ++this.generation;
      clearTimeout(this.loadTimer);
      clearInterval(this.arTimer);
      this.pending = false;
      this.stage.dataset.state = 'error';
      this.loading.hidden = true;
      this.error.hidden = false;
      this.xr.hidden = true;
      this.xr.disabled = true;
      this.stage.dataset.ar = 'no';
      if (!this.stage.hidden && this.stage.contains(document.activeElement)) this.error.querySelector('button').focus({ preventScroll: true });
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      this.close(false);
      clearTimeout(this.loadTimer);
      clearInterval(this.arTimer);
      this.observer.disconnect();
      this.thumb.removeEventListener('click', this.onThumb);
      this.stage.removeEventListener('click', this.onStage, true);
      this.host.removeEventListener('click', this.onHostClick, true);
      this.host.removeEventListener('change', this.onChange);
      this.host.removeEventListener('keydown', this.onKey);
      ++this.generation;
      this.pending = false;
      this.pm.modelViewerUI?.destroy?.();
      this.pm.modelViewerUI = null;
      this.pm.querySelectorAll('model-viewer, .shopify-model-viewer-ui').forEach(node => node.remove());
      this.pm.removeAttribute('loaded');
      this.viewer = null;
      this.stage.dataset.state = 'idle';
      this.loading.hidden = true;
      this.error.hidden = true;
      this.xr.hidden = true;
      this.xr.disabled = true;
      this.stage.dataset.ar = 'no';
      this.append(this.thumbItem, this.stage);
      this.initialized = false;
    }
  });
})();
